import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { useParams } from "react-router-dom"

import CampaignLinksStatus from "@components/campaign_links_status"
import CampaignLinksTable from "@components/campaign_links_table"
import CampaignLinksToggle from "@components/dialogs/campaign_links_toggle"
import { useCustomerPageContext } from "@contexts/customer"
import {
  CAMPAIGN_LINKS_QUERY_KEYS,
  getAdminCampaignLinks
} from "@domain/campaign_links"
import useMe from "@hooks/use_me"
import { type CampaignLinksToggleTarget } from "@models/campaign_links"
import { RoleTypes } from "__generated__/graphql"

type Toggle = { target: CampaignLinksToggleTarget; disable: boolean }

export default function CustomerCampaignLinksPage() {
  const { translate } = useCustomerPageContext()
  const { id = "" } = useParams()
  const { me } = useMe()
  const [toggle, setToggle] = useState<Toggle | null>(null)

  // Cosmetic only — the API enforces the ADMIN role on the write.
  const canEdit = me?.role === RoleTypes.Admin

  const { data, isLoading } = useQuery({
    queryKey: [CAMPAIGN_LINKS_QUERY_KEYS.customer, id],
    queryFn: async () => (await getAdminCampaignLinks(id)).data ?? null,
    enabled: !!id
  })

  if (isLoading) return <CircularProgress size={24} />
  if (!data) return null

  const customerLabel = data.customerName || data.customerId

  return (
    <Stack gap={2} sx={{ pt: 2 }}>
      <Alert severity="info">{translate("campaign_links_customer_hint")}</Alert>

      <Stack direction="row" alignItems="center" gap={2}>
        <Typography variant="subtitle1">
          {translate("campaign_links_customer_links")}
        </Typography>
        <CampaignLinksStatus status={data.links} />
        {canEdit && (
          <Button
            size="small"
            variant="outlined"
            color={data.links.disabled ? "primary" : "error"}
            onClick={() =>
              setToggle({
                target: {
                  kind: "customer",
                  customerId: data.customerId,
                  label: customerLabel
                },
                disable: !data.links.disabled
              })
            }
          >
            {translate(
              data.links.disabled
                ? "campaign_links_turn_on_all"
                : "campaign_links_turn_off_all"
            )}
          </Button>
        )}
      </Stack>

      <CampaignLinksTable
        rows={data.campaigns}
        canEdit={canEdit}
        onToggle={(row) =>
          setToggle({
            target: {
              kind: "campaign",
              campaignId: row._id,
              label: row.title || row._id
            },
            disable: !row.links.disabled
          })
        }
      />

      {!!toggle && (
        <CampaignLinksToggle
          isOpen={!!toggle}
          setClose={() => setToggle(null)}
          target={toggle.target}
          disable={toggle.disable}
        />
      )}
    </Stack>
  )
}
